import React, { type FC, type JSX } from 'react';
import { CloseIconContainer, ModalHeaderContainer } from './styled';
import CloseIcon from '../../CloseIcon';
import { HeaderTitle, useCloseLabel } from '../modalHeaderCommon';

type ModalHeaderProps = {
  handleClose?: () => void;
  removeShadow?: boolean;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  closeOnRight?: boolean;
  // Accessible name for the icon-only close button. Defaults to the translated "Close";
  // pass a descriptive one (e.g. "Close Your trip") so screen readers announce context.
  closeLabel?: string;
  // Decorative extras such as a loading line, drawn in the header bar after the title and outside the heading.
  adornment?: React.ReactNode;
};

const BasicModalHeader: FC<ModalHeaderProps> = ({
  children,
  removeShadow,
  className = '',
  handleClose,
  style,
  closeOnRight,
  closeLabel,
  adornment,
}): JSX.Element => {
  const closeName = useCloseLabel(closeLabel);
  return (
    <ModalHeaderContainer removeShadow={removeShadow} className={className} style={style}>
      {handleClose && (
        <CloseIconContainer
          type="button"
          aria-label={closeName}
          $closeOnRight={closeOnRight}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            // Radix Dialog portals the modal; React synthetic events still bubble through the
            // React tree back to the component that triggered the open. Stop propagation here to
            // prevent the parent opener (e.g., Feature button) from re-opening the modal.
            e.stopPropagation();
            handleClose();
          }}
        >
          <CloseIcon />
        </CloseIconContainer>
      )}
      <HeaderTitle>{children}</HeaderTitle>
      {adornment}
    </ModalHeaderContainer>
  );
};

export default BasicModalHeader;
